import { useContext, useEffect, useState } from 'react'
import { Column } from 'react-table'
import Table from '../../../global/components/table/Table'
import { IVan } from '../../../../../app/entities/Van'
import { IVanBatteryDataBinding } from '../../../../../app/entities/VanBatteryDataBinding'
import { handleColumnFilterUpdate } from '../../../global/components/table/helpers/multiColumnFilters'
import retrieveData from '../../../global/components/table/helpers/retrieveData'
import BasicFilter from '../../../global/components/table/helpers/BasicFilter'
import AssignUserCell from '../../../global/components/assign-user/AssignUserCell'
import { PERMISSIONS } from '../../../global/constants/permissions'
import hasPermission from '../../../global/utils/user/has-permission'
import { UserContext } from '../../../base/components/base-container/BaseContainer'
import { fetchData } from '../../../global/utils/fetch'
import { getAuditAssignToUserList } from '../api'
import { IUser } from '../../../../../app/entities/User'
import NoFilter from '../../../global/components/table/helpers/NoFilter'
import { ILocationFilters } from '../../main/AuditsMain'
import { formatDate } from '../../../global/utils/date/mm-dd-yyyy'
import VanDownModal from '../van-down-modal/VanDownModal'
import PrimaryButton from '../../../global/components/buttons/primary-button/PrimaryButton'
import { OPERATING_STATUSES, VAN_STATUS_SOURCES } from '../../../global/constants/van'

// Helper to get unresolved inoperative entries from van history
function getUnresolvedInoperativeEntries(van: IVan) {
  if (!van.operatingStatusHistory) return []

  // Get all inoperative entries (excluding tech fix requests) that are NOT resolved
  const inoperativeEntries = van.operatingStatusHistory.filter(
    (h: any) =>
      h.operatingStatus === OPERATING_STATUSES.INOPERATIVE &&
      h.source !== VAN_STATUS_SOURCES.TECH_FIX_REQUEST &&
      !h.resolved
  )

  // Sort by created date descending (most recent first)
  return inoperativeEntries.sort((a: any, b: any) =>
    new Date(b.created).getTime() - new Date(a.created).getTime()
  )
}

// Helper to get the most recent tech fix request
function getTechFixRequest(van: IVan) {
  if (!van.operatingStatusHistory) return null
  return van.operatingStatusHistory.find((h: any) => h.source === VAN_STATUS_SOURCES.TECH_FIX_REQUEST)
}

async function fetchAssignToUserList(setUserList: Function) {
  const users = await fetchData<IUser[]>(
    getAuditAssignToUserList(
      PERMISSIONS.AUDITS.ENTRIES.INSTALLATION_AUDITS_ASSIGNEE.NAME,
    ),
  )
  setUserList(users)
}

function assignUserToVan(vanObjectId: string, assignedTo: string | null) {
  const body = {
    assignedTo,
  }
  return {
    method: 'PUT',
    url: `${process.env.REACT_APP_ORIGIN}/vans/${vanObjectId}/assigned-to`,
    body,
  }
}

interface IVanDownAuditsTableProps {
  setRefetchIndex: Function
  refetchIndex: number
  locationFilters: ILocationFilters
}

function VanDownAuditsTable(props: IVanDownAuditsTableProps) {
  const { setRefetchIndex, refetchIndex, locationFilters } = props
  const { user } = useContext(UserContext)
  const [usersList, setUsersList] = useState<IUser[]>([])
  const [tableState, setTableState] = useState({
    data: [],
    isLoading: false,
    offSet: 0,
    pageSize: 100,
    count: 0,
  })
  const [columnFilters, setColumnFilters] = useState({})
  const [vanDownModalOpen, setVanDownModalOpen] = useState(false)
  const [selectedVan, setSelectedVan] = useState<IVan | null>(null)
  const vanDownTableEndpoint = `${process.env.REACT_APP_ORIGIN}/vans/inoperative?`

  function onColumnFilterUpdate(filtersUpdate: object) {
    handleColumnFilterUpdate(filtersUpdate, columnFilters, setColumnFilters)
  }

  function retrieveTableData() {
    retrieveData(setTableState, {
      endpoint: vanDownTableEndpoint,
      pageSize: tableState.pageSize,
      offSet: tableState.offSet,
      columnFilters,
      selectedLocationId: locationFilters.locationFilter,
    })
  }

  useEffect(() => {
    retrieveTableData()
  }, [refetchIndex, locationFilters, tableState.pageSize, tableState.offSet])

  useEffect(() => {
    fetchAssignToUserList(setUsersList)
  }, [])

  const columns: Column<IVan>[] = [
    {
      Header: 'Status',
      accessor: 'vanFixPendingApproval' as any,
      Filter: NoFilter(),
      Cell: ({ row: { original } }: { row: { original: IVan } }) => {
        if (original.vanFixPendingApproval) {
          return (
            <div
              style={{
                backgroundColor: '#FFA500',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              Fix Pending Approval
            </div>
          )
        }
        if (original.activeWithUnresolvedIssue) {
          return (
            <div
              style={{
                backgroundColor: '#FF9800',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontWeight: 'bold',
                textAlign: 'center',
              }}
            >
              Active with Unresolved Issue
            </div>
          )
        }
        return <div style={{ textAlign: 'center' }}>Inoperative</div>
      },
    },
    {
      Header: 'Assigned To',
      accessor: 'assignedTo',
      Filter: BasicFilter({
        onChange: (val: string) =>
          onColumnFilterUpdate({ assignedTo: val }),
        onEnter: retrieveTableData,
      }),
      Cell: ({ row: { original } }: { row: { original: IVan } }) => {
        const assignedTo = original.assignedTo
        const objectId = original.objectId
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <AssignUserCell
              assignedTo={assignedTo ?? undefined}
              usersList={usersList}
              objectId={objectId}
              retrieveTableData={retrieveTableData}
              disabled={
                !hasPermission(user).allow(
                  PERMISSIONS.AUDITS.CATEGORY,
                  PERMISSIONS.AUDITS.ENTRIES.INSTALLATION_AUDITS_ASSIGNER.NAME,
                )
              }
              assignUserTo={assignUserToVan}
            />
          </div>
        )
      },
    },
    {
      Header: 'Van #',
      accessor: 'name',
      Filter: NoFilter(),
      Cell: ({ row: { original } }: { row: { original: IVan } }) => {
        return <>{original.name || '-'}</>
      },
    },
    {
      Header: 'Location',
      accessor: 'location' as any,
      Filter: NoFilter(),
      Cell: ({ row: { original } }: { row: { original: IVan } }) => {
        return <>{original.location?.name || '-'}</>
      },
    },
    {
      Header: 'Battery SOCs',
      accessor: 'vanBatteryDataBindings' as any,
      Filter: NoFilter(),
      Cell: ({ row: { original } }: { row: { original: IVan } }) => {
        const batteries = original.vanBatteryDataBindings || []
        if (batteries.length === 0) return <>-</>
        const socs = batteries
          .map((b: IVanBatteryDataBinding) => `${b.mostRecentBatteryData?.stateOfCharge || 0}%`)
          .join(', ')
        return <>{socs}</>
      },
    },
    {
      Header: 'InOp Start Date',
      accessor: 'operatingStatusStartDate',
      Filter: NoFilter(),
      Cell: ({ row: { original } }: { row: { original: IVan } }) => {
        if (!original.operatingStatusStartDate) return <>-</>
        const date = new Date(original.operatingStatusStartDate)
        const formattedDate = formatDate(date)
        return <>{formattedDate}</>
      },
    },
    {
      Header: '#Days InOp',
      accessor: 'id' as any,
      Filter: NoFilter(),
      Cell: ({ row: { original } }: { row: { original: IVan } }) => {
        if (!original.operatingStatusStartDate) return <>-</>
        const startDate = new Date(original.operatingStatusStartDate)
        const today = new Date()
        const diffTime = Math.abs(today.getTime() - startDate.getTime())
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        return <>{diffDays}</>
      },
    },
    {
      Header: 'Issue',
      accessor: 'issue',
      Filter: NoFilter(),
      Cell: ({ row: { original } }: { row: { original: IVan } }) => {
        const unresolvedEntries = getUnresolvedInoperativeEntries(original)

        if (unresolvedEntries.length === 0) return <>-</>

        const mostRecent = unresolvedEntries[0]
        const fullIssue = mostRecent.issue || 'No description'

        // Parse to extract just the issue (before " - ")
        const issuePart = fullIssue.includes(' - ')
          ? fullIssue.split(' - ')[0].trim()
          : fullIssue

        // Show count indicator if there are multiple unresolved issues
        if (unresolvedEntries.length > 1) {
          return <>{issuePart} (+{unresolvedEntries.length - 1})</>
        }

        return <>{issuePart}</>
      },
    },
    {
      Header: 'Reported By',
      accessor: 'objectId' as any,
      Filter: NoFilter(),
      Cell: ({ row: { original } }: { row: { original: IVan } }) => {
        const unresolvedEntries = getUnresolvedInoperativeEntries(original)

        if (unresolvedEntries.length === 0) return <>-</>

        const mostRecent = unresolvedEntries[0]
        return <>{mostRecent.reportedBy || 'Unknown'}</>
      },
    },
    {
      Header: 'Notes',
      accessor: 'updated' as any,
      Filter: NoFilter(),
      Cell: ({ row: { original } }: { row: { original: IVan } }) => {
        const unresolvedEntries = getUnresolvedInoperativeEntries(original)

        if (unresolvedEntries.length === 0) return <>-</>

        const mostRecent = unresolvedEntries[0]
        const fullIssue = mostRecent.issue || ''

        // Parse to extract just the notes (after " - ")
        if (fullIssue.includes(' - ')) {
          const notesPart = fullIssue.split(' - ').slice(1).join(' - ').trim()
          return <>{notesPart || '-'}</>
        }

        return <>-</>
      },
    },
    {
      Header: '',
      accessor: 'actions' as any,
      Filter: NoFilter(),
      Cell: ({ row: { original } }: { row: { original: IVan } }) => {
        return (
          <div onClick={(e) => e.stopPropagation()}>
            <PrimaryButton
              buttonName="Resolve"
              onClick={() => {
                setSelectedVan(original)
                setVanDownModalOpen(true)
              }}
            />
          </div>
        )
      },
    },
  ]

  return (
    <div style={{ width: '100%' }}>
      <Table
        columns={columns}
        data={tableState.data ?? []}
        onRowClick={() => {}}
        isLoading={tableState.isLoading}
        isPaginated
        pagination={{ setTableState, tableState }}
        isTableTopperPresent={false}
      />
      <VanDownModal
        isOpen={vanDownModalOpen}
        onClose={() => setVanDownModalOpen(false)}
        vanObjectId={selectedVan?.objectId || null}
        vanName={selectedVan?.name || ''}
        currentStatus={selectedVan?.operatingStatus || ''}
        currentIssue={selectedVan?.issue || ''}
        inOpDate={selectedVan?.operatingStatusStartDate || null}
        vanFixPendingApproval={selectedVan?.vanFixPendingApproval || false}
        refetchTable={retrieveTableData}
      />
    </div>
  )
}

export default VanDownAuditsTable
